import { useMemo } from "react";
import { useAppSelector } from "@store/index";
import { chartConfigSelector } from "@store/slices/chart-config-slice";
import { useGetDatasetQuestionVariablesByElementQuery } from "@store/api/dataset-api";
import {
  getGroupedVariableLabel,
  getGroupedValueLabel,
} from "@utils/grouped-bar-utils";

export interface GroupedUniverseItem {
  id: string;
  label: string;
  // Response (machine) label variants used to reconcile chart-data ids.
  matchLabels: string[];
}

const uniqueTruthy = (values: Array<string | undefined | null>): string[] =>
  Array.from(new Set(values.filter((v): v is string => !!v && v !== "")));

/**
 * Loads the grid question's full variable and value sets (survey order
 * preserved) for the grouped bar selectors. Unlike the bar chart, grouped bar
 * always needs these regardless of any focus selection.
 */
export function useGroupedBarVariables() {
  const { datasetId, questionId } = useAppSelector(chartConfigSelector);

  const { data, isFetching } = useGetDatasetQuestionVariablesByElementQuery(
    { id: datasetId!, dataset_element_id: questionId! },
    { skip: !datasetId || !questionId },
  );

  const variables = useMemo<GroupedUniverseItem[]>(
    () =>
      (data?.variables ?? []).map((v) => ({
        id: v.datasetElementId,
        label: getGroupedVariableLabel(v),
        matchLabels: uniqueTruthy([
          v.label,
          v.rowtitle,
          v.coltitle,
          v.title,
          v.value,
        ]),
      })),
    [data],
  );

  const values = useMemo<GroupedUniverseItem[]>(
    () =>
      (data?.values ?? []).map((v) => ({
        id: v.datasetElementId,
        label: getGroupedValueLabel(v),
        matchLabels: uniqueTruthy([
          v.label,
          v.title,
          v.rowtitle,
          v.coltitle,
          v.value != null ? String(v.value) : undefined,
        ]),
      })),
    [data],
  );

  return { variables, values, isFetching };
}
